import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { isValidEmail } from 'utils/validations';
import { ClinicOtherInfoFormData } from 'models/forms';
import { emptyOtherInfoFormData } from '../clinic-form-helper';
import { Clinic } from 'models/clinic';

type OtherInfoFormProps = {
    clinic?: Partial<Clinic>;
    onChange: (formData: ClinicOtherInfoFormData, isFormValid: boolean) => void;
};
const OtherInfoForm: FC<OtherInfoFormProps> = ({ clinic, onChange }: OtherInfoFormProps) => {
    const { t: translate } = useTranslation(['organization']);

    const validate = (formData: ClinicOtherInfoFormData) => {
        const errors: Record<string, string> = {};
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        if (formData.invoiceEmail && !isValidEmail(formData.invoiceEmail)) {
            errors.invoiceEmail = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    useEffect(() => {
        formik.setValues({
            vatNo: clinic?.invoicing?.vatNo || '',
            costCenter: clinic?.invoicing?.costCenter || '',
            gln: clinic?.gln || '',
            email: clinic?.contact?.communication?.email || '',
            phone: clinic?.contact?.communication?.phone || '',
            site: clinic?.contact?.communication?.site || '',
            invoiceEmail: clinic?.invoicing?.communication?.email || '',
        });
    }, [clinic]);

    const formik = useFormik<ClinicOtherInfoFormData>({
        initialValues: emptyOtherInfoFormData(),
        validate,
        onSubmit: async (formData) => {
            onChange(formData, formik.isValid);
        },
    });

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    label={translate('common:EMAIL')}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    {...formik.getFieldProps('email')}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label={translate('organization:INVOICE_EMAIL')}
                    fullWidth
                    helperText={formik.touched.invoiceEmail && formik.errors.invoiceEmail}
                    error={formik.touched.invoiceEmail && Boolean(formik.errors.invoiceEmail)}
                    {...formik.getFieldProps('invoiceEmail')}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label={translate('common:PHONE')} fullWidth {...formik.getFieldProps('phone')} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label={translate('common:WEBSITE')} fullWidth {...formik.getFieldProps('site')} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label={translate('organization:VAT_NUMBER')} fullWidth {...formik.getFieldProps('vatNo')} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    label={translate('client-directory:COST_CENTER')}
                    fullWidth
                    {...formik.getFieldProps('costCenter')}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label={translate('organization:GLN_NUMBER')} fullWidth {...formik.getFieldProps('gln')} />
            </Grid>
        </Grid>
    );
};

export default OtherInfoForm;
